import React from "react";
import { ShareSection } from "../../components/template";
import { Helmet } from "react-helmet";

const Blog5 = () => {

  const hashtagsBottom = [
    "#HealthyLifestyle",    "#MicroHabits",
    "#MindfulLiving",
    "#DailyRituals",
    "#FocusAndCalm",
    "#AyurvedaInspired",
    "#MindBodyBalance",
    "#TinyChangesBigImpact",

    "#ConsistencyWins",
  ];

  const HashtagSection = ({ tags }) => (
    <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
      {tags.map((tag, index) => (
        <span
          key={index}
          className="bg-light text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 hover:bg-red-700 hover:text-white"
        >
          {tag}
        </span>
      ))}
    </div>
  );

  return (
    <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed">
      <Helmet>
        <meta
          name="title"
          content="10 Micro Habits That Create Massive Shifts"
        />
        <meta
          name="description"
          content="Discover 10 small daily habits that boost your energy, focus, and inner calm — without changing your entire lifestyle."
        />
        <meta
          name="keywords"
          content="micro habits, lifestyle habits, self improvement, wellness, productivity, focus, calm, Ayurveda, daily rituals, Param Life"
        />
        <title>10 Micro Habits That Create Massive Shifts</title>
      </Helmet>

      {/* HEADER */}
      <header className="text-center mb-8">
        <h1 className="text-secondary text-4xl font-bold mb-6">
          10 Micro Habits That Create Massive Shifts
        </h1>
                <img
          src="/assets/knowyourself/blogs/blog5.png"
          alt="After 30 wellness"
          className="w-[80%] max-w-xl h-auto max-h-[250px] rounded-lg mb-5 shadow-md mx-auto object-cover"
        />

        <p className="text-gray-600 italic">
          We often wait for big moments to change our lives — new jobs, retreats,
          or perfect plans. But real transformation begins quietly. It starts in
          the smallest choices you repeat every single day. Here are 10 micro
          habits that may look simple — but they can completely shift your
          focus, energy, and inner peace.
        </p>
      </header>


      {/* CONTENT SECTIONS */}
      <section className="space-y-6 text-lg text-gray-800">
        <p>
          Most people think transformation needs big, dramatic changes — like
          quitting your job, going to the Himalayas, or doing a 30-day detox.
          But here’s the truth: your life changes not when you do something huge
          once, but when you do something small every single day.
        </p>

        <p>
          Tiny habits are powerful because they rewire your energy quietly. They
          don’t demand motivation — they build momentum. Here are 10 micro
          habits that seem small at first but can completely shift your focus,
          mood, and clarity over time.
        </p>

        {/* 1 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          1. No Screens While Eating
        </h2>
        <p>
          When was the last time you actually tasted your food? Most people eat
          while watching reels, checking emails, or scrolling through the news.
          Your body might be eating, but your mind is somewhere else.
        </p>
        <p>
          Ayurveda says — “If your mind isn’t present, you can’t digest well.”
          When you eat with awareness, your digestion, mood, and energy improve
          instantly.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> For
          one meal a day, keep your phone away. Just eat. Feel the textures, the
          taste, the smell. You’ll realize how peaceful eating can be when your
          mind joins in.
        </p>

        {/* 2 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          2. 10 Minutes of Meditation
        </h2>
        <p>
          You don’t need an app, a guru, or an hour. Just 10 minutes of
          stillness can reset your nervous system like nothing else. Most people
          say, “I can’t meditate.” But meditation isn’t about stopping thoughts
          — it’s about watching them without reacting.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Sit
          comfortably. Close your eyes. Breathe slowly. If your mind wanders,
          bring it back to the breath. That’s it. That’s meditation.
        </p>

        {/* 3 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          3. Avoid Late-Night Scrolling
        </h2>
        <p>
          Nothing good ever comes after 11 PM on your phone. You’re not
          learning — you’re escaping. Those extra 20 minutes of scrolling
          silently rob your sleep quality, mood, and morning focus.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Set a
          hard stop — no screen after 10:30 PM. Use “Do Not Disturb” or grayscale
          mode. Let your brain rest before your body does.
        </p>

        {/* 4 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          4. Eat Your Dinner Before 8 PM
        </h2>
        <p>
          Late dinners are one of the most ignored reasons behind fatigue,
          bloating, and restless sleep. Your digestion slows after sunset — that’s
          how nature designed it.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Eat
          your dinner before 8 PM. If you can’t, keep it light — soups, khichdi,
          or vegetables. You’ll sleep deeper and wake up lighter.
        </p>

        {/* 5 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          5. No Phone for 30 Minutes After Waking Up
        </h2>
        <p>
          Your morning mind is like wet clay — whatever you touch first shapes
          your energy. If you grab your phone right after waking, you invite
          chaos before calm.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Don’t
          touch your phone for the first 30 minutes. Stretch, sit quietly, or
          step outside for sunlight. Let your mind wake up before your screen
          does.
        </p>

        {/* 6 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          6. Hydrate Before Your First Tea or Coffee
        </h2>
        <p>
          Most people wake up and reach for caffeine. But overnight, your body
          gets dehydrated. Caffeine on an empty system only stresses your
          organs.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Start
          your day with one glass of warm water. Add a pinch of rock salt or
          lemon if you like. Then have your chai — your energy will feel smoother,
          not spiked.
        </p>

        {/* 7 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          7. Journal Your Day in Two Lines Every Night
        </h2>
        <p>
          You don’t need a fancy notebook or deep reflections. Just two lines:
          What went well today? and What did I learn? This tiny act of reflection
          builds awareness — and awareness builds growth.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Keep a
          small notebook by your bed. Write two lines before sleeping. You’ll
          notice how much more grateful and grounded you feel.
        </p>

        {/* 8 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          8. Put Your Phone Away 30 Minutes Before Bed
        </h2>
        <p>
          Your phone keeps your brain in alert mode. Even if you scroll “just
          for five minutes,” your sleep quality drops. The mind needs closure — a
          signal that the day is done.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> 30
          minutes before bed, charge your phone outside your room. Read, stretch,
          or listen to soft music instead. You’ll fall asleep faster — and stay
          asleep longer.
        </p>

        {/* 9 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          9. Set an Intention Each Morning (In One Sentence)
        </h2>
        <p>
          Before your day begins, decide how you want to feel — not just what you
          want to do. Intentions anchor your energy and guide your reactions.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Write
          or say your intention aloud each morning. You’ll notice how your day
          naturally aligns with it.
        </p>

        {/* 10 */}
        <h2 className="text-primary text-2xl font-semibold mt-8">
          10. Sit in Natural Light for 10 Minutes in the Morning
        </h2>
        <p>
          Sunlight is nature’s medicine. It wakes your brain, regulates hormones,
          and balances your sleep cycle. Just 10 minutes of natural light can
          reset your body clock better than any supplement.
        </p>
        <p>
          <span className="text-[#578d48] font-semibold">Try this:</span> Step
          outside after waking. Sit quietly with your tea. Let sunlight hit your
          eyes and skin. It’ll lift your mood and calm your mind instantly.
        </p>
      </section>


      {/* FINAL THOUGHTS */}
      <section className="mt-10 space-y-4 text-lg text-gray-800">
        <h2 className="text-primary text-2xl font-semibold">Final Thoughts</h2>
        <p>
          Small habits don’t look powerful at first. But done daily, they quietly
          reprogram your mind and body. You don’t need to do all 10 at once —
          pick 2 or 3 that feel easiest and start there.
        </p>
        <p>
          Because in the end, growth is not about massive effort. It’s about
          consistent alignment with what feels good for your body and soul.
        </p>
        <p>
          Peace doesn’t come from doing more — it comes from doing what truly
          matters, just a little more often.
        </p>
      </section>

      <HashtagSection tags={hashtagsBottom} />

      <ShareSection />
    </article>
  );
};

export default Blog5;
